import { Component, OnInit, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Apollo, gql } from 'apollo-angular';
import { ToastrService } from 'ngx-toastr';
import { cloneDeep } from 'lodash';

import { HeaderComponent } from '../header/header.component';
import { NavComponent } from '../navigation/nav.component';
import { AuthStore } from '@store/auth.store';
import { ServiceAvailability } from '@interfaces/models.interface';
import { isUnsupportedByApi } from '../../../shared/utils/graphql-support.util';
import {
  isMissingPermissionError,
  MISSING_PERMISSION_MESSAGE,
} from '@shared/utils/admin-permission-error.util';
import { AdminPermission } from '@enums/admin-permission.enum';
import { AdminPermissionService } from '@services/admin-permission.service';

const SERVICE_FIELDS = `
  serviceType
  isEnabled
  unavailableMessage
  dateUpdated
`;

const SERVICE_AVAILABILITY = gql`
  query serviceAvailability {
    serviceAvailability { ${SERVICE_FIELDS} }
  }
`;

const UPDATE_SERVICE_AVAILABILITY = gql`
  mutation updateServiceAvailability($input: UpdateServiceAvailabilityInput!) {
    updateServiceAvailability(input: $input) { ${SERVICE_FIELDS} }
  }
`;

const SERVICE_LABELS: Record<string, string> = {
  express: 'OK! Express',
  okride: 'OK! Ride',
  mototaxi: 'OK! MotoTaxi',
  oktaxi: 'OK! Taxi',
};

export const MAX_MESSAGE_LENGTH = 200;

/**
 * Service availability (CMS) — the on/off switch per bookable service.
 *
 * Update only: the four rows are seeded by migration and the apps render
 * whatever is here, so adding or removing a service is a release, not a CMS
 * action.
 *
 * Worth not confusing with the two neighbouring flags: Service Fee's
 * `isActive` decides whether a *fee configuration* applies (an inactive one
 * falls back to the default fee and the service keeps running), and TNVS
 * Fare's `status` retires a single vehicle class. This page is the only one
 * that stops a service being booked at all.
 */
@Component({
  selector: 'app-services-cms',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, HeaderComponent, NavComponent],
  templateUrl: './services-cms.component.html',
  styleUrl: './services-cms.component.css',
})
export class ServicesCmsComponent implements OnInit {
  constructor(
    private apollo: Apollo,
    private modalService: NgbModal,
    private toastr: ToastrService,
    private router: Router,
    private authStore: AuthStore,
    private permissions: AdminPermissionService,
  ) {}

  /** Editing is offered only with `services.manage` — the API refuses it otherwise. */
  get canManage(): boolean {
    return this.permissions.can(AdminPermission.ServicesManage);
  }

  public readonly maxMessageLength = MAX_MESSAGE_LENGTH;

  public services: ServiceAvailability[] = [];
  public isLoading = false;
  public loadError: 'none' | 'unsupported' | 'failed' = 'none';

  public showDetails = false;
  public enableEdit = false;
  public isSaving = false;
  public detailViewed: ServiceAvailability | null = null;

  public form: { isEnabled: boolean; unavailableMessage: string } = {
    isEnabled: true,
    unavailableMessage: '',
  };

  ngOnInit(): void {
    this.fetchServices();
  }

  serviceLabel(serviceType: string): string {
    return SERVICE_LABELS[serviceType] ?? serviceType;
  }

  get disabledCount(): number {
    return this.services.filter((service) => !service.isEnabled).length;
  }

  fetchServices(): void {
    this.isLoading = true;
    this.loadError = 'none';

    this.apollo
      .watchQuery({ fetchPolicy: 'network-only', query: SERVICE_AVAILABILITY })
      .valueChanges.subscribe({
        next: (res: any) => {
          this.isLoading = false;
          const rows: ServiceAvailability[] = res?.data?.['serviceAvailability'] ?? [];
          const order = Object.keys(SERVICE_LABELS);
          this.services = cloneDeep(rows).sort(
            (a, b) => order.indexOf(a.serviceType) - order.indexOf(b.serviceType),
          );

          if (this.detailViewed) {
            const refreshed = this.services.find(
              (service) => service.serviceType === this.detailViewed!.serviceType,
            );
            if (refreshed) this.detailViewed = refreshed;
          }
        },
        error: (error) => {
          this.isLoading = false;
          if (this.handledAuthError(error)) return;
          this.loadError = isUnsupportedByApi(error) ? 'unsupported' : 'failed';
        },
      });
  }

  viewDetails(service: ServiceAvailability): void {
    this.detailViewed = service;
    this.showDetails = true;
    this.enableEdit = false;
    this.resetFormFromRecord();
  }

  returnToList(): void {
    this.showDetails = false;
    this.enableEdit = false;
    this.detailViewed = null;
    this.form = { isEnabled: true, unavailableMessage: '' };
  }

  enableEditForm(): void {
    this.enableEdit = true;
    this.resetFormFromRecord();
  }

  disableEditForm(): void {
    this.enableEdit = false;
    this.resetFormFromRecord();
  }

  private resetFormFromRecord(): void {
    const record = this.detailViewed;
    this.form = {
      isEnabled: record?.isEnabled ?? true,
      unavailableMessage: record?.unavailableMessage ?? '',
    };
  }

  /** True when the pending save would take a live service offline. */
  get isTurningOff(): boolean {
    return !!this.detailViewed?.isEnabled && !this.form.isEnabled;
  }

  private validationError(): string | null {
    if (this.form.unavailableMessage.trim().length > MAX_MESSAGE_LENGTH) {
      return `Message must not exceed ${MAX_MESSAGE_LENGTH} characters.`;
    }
    return null;
  }

  confirmUpdate(modal: TemplateRef<any>): void {
    const error = this.validationError();
    if (error) {
      this.toastr.error(error);
      return;
    }

    this.modalService.open(modal, { backdrop: 'static', keyboard: false, size: 'lg' });
  }

  updateService(successModal: TemplateRef<any>): void {
    const record = this.detailViewed;
    const error = this.validationError();
    if (!record || error) {
      if (error) this.toastr.error(error);
      return;
    }

    this.isSaving = true;

    const input = {
      serviceType: record.serviceType,
      isEnabled: this.form.isEnabled,
      // Always sent: an empty string is how the API is told to clear a custom
      // message back to the app's default copy.
      unavailableMessage: this.form.unavailableMessage.trim(),
    };

    this.apollo
      .mutate({ mutation: UPDATE_SERVICE_AVAILABILITY, variables: { input } })
      .subscribe({
        next: ({ data }: any) => {
          this.isSaving = false;
          const updated: ServiceAvailability | undefined =
            data?.['updateServiceAvailability'];
          if (!updated) {
            this.toastr.error('The service was not updated. Please try again.');
            return;
          }

          this.detailViewed = cloneDeep(updated);
          this.services = this.services.map((service) =>
            service.serviceType === updated.serviceType ? cloneDeep(updated) : service,
          );
          this.resetFormFromRecord();

          this.modalService.dismissAll();
          this.enableEdit = false;
          this.modalService.open(successModal, {
            backdrop: 'static',
            keyboard: false,
            size: 'lg',
          });
        },
        error: (error) => {
          this.isSaving = false;
          this.modalService.dismissAll();
          if (this.handledAuthError(error)) return;
          this.toastr.error(error?.message || 'Error updating the service');
        },
      });
  }

  closeAllModals(): void {
    this.modalService.dismissAll();
  }

  private handledAuthError(error: any): boolean {
    // Refused by the admin's roles, not a dead session: say so and stay signed in.
    if (isMissingPermissionError(error)) {
      this.toastr.error(MISSING_PERMISSION_MESSAGE);
      return true;
    }

    const isAuthError =
      error?.message === 'Unauthorized' ||
      error?.networkError?.status === 403 ||
      error?.graphQLErrors?.[0]?.extensions?.status === 403 ||
      error?.errors?.[0]?.extensions?.status === 403;

    if (!isAuthError) return false;

    this.authStore.logout().subscribe(() => {
      this.router.navigate(['/admin/login']);
    });
    return true;
  }
}
